import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, ShieldCheck, Award, CheckCircle, ArrowRight, Calendar } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import BrandLogo from '../../components/common/BrandLogo';

export default function AboutPage() {
  const { t } = useLanguage();

  return (
    <div className="bg-[#05080e] min-h-screen text-slate-200">
      {/* Header Banner */}
      <section className="bg-[#070c16] text-white py-14 sm:py-20 border-b border-[#141d2d] text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:underline mb-4"
          >
            &larr; {t('back_to_home', 'Bosh sahifaga qaytish')}
          </Link>
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
            {t('firm_name_badge', 'JIZZAX ADVOKATLARI')}
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-slate-100">
            {t('nav_about', 'Advokatlar Haqida')}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-4 font-light leading-relaxed">
            {t('hero_subheadline', 'Murakkab tijorat nizolari, korporativ bitimlar va jinoiy himoya bo‘yicha O‘zbekiston miqyosidagi yetakchi advokatlar jamoasi.')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-24 bg-[#05080e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="space-y-6">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#c59b27] font-semibold block">
                {t('about_philosophy_tag', 'OUR PHILOSOPHY & VALUES')}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-100 leading-tight">
                {t('about_mission_title', 'Bizning Missiyamiz va Professional Falsafamiz')}
              </h2>
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed font-light">
                {t('about_mission_desc_1', 'Jizzax Advokatlari — O‘zbekistonda yetakchi yuridik amaliyot jamoalaridan biridir. Biz mijozlarimizga shunchaki maslahat bermaymiz, balki ularning buzilgan huquqlarini tiklashda kafolatlovchi strategik yechimlarni taqdim etamiz.')}
              </p>
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed font-light">
                {t('about_mission_desc_2', 'Jamoamiz a’zolari Oliy toifali advokat litsenziyalariga ega bo‘lib, murakkab aksiyadorlik nizolari, sud himoyasi va yirik iqtisodiy ishlarda yuksak natijalarga erishgan.')}
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden border border-[#c59b27]/30 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1000"
                alt="Law firm meeting room"
                className="object-cover w-full h-[400px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05080e] via-transparent to-transparent" />
            </div>
          </div>

          {/* Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-[#141d2d]">
            <div className="p-8 rounded-xl bg-[#090e18] border border-[#1b2b46] hover:border-[#c59b27]/80 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c59b27]/12 transition-all duration-300 group relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-28 h-28 bg-[#c59b27]/0 group-hover:bg-[#c59b27]/15 rounded-full blur-2xl transition-all duration-500 pointer-events-none" />
              <Scale className="w-8 h-8 text-[#c59b27] mb-4 group-hover:scale-120 group-hover:-translate-y-1 transition-all duration-300" />
              <h3 className="font-serif font-bold text-slate-100 text-lg mb-2 group-hover:text-amber-200 transition-colors">
                {t('pillar_rule_of_law', 'Qonun Ustuvorligi')}
              </h3>
              <p className="text-slate-400 text-xs leading-relaxed font-light">
                {t('pillar_rule_of_law_desc', 'Har qanday vaziyatda qonun talablariga to‘liq rioya qilgan holda, mijoz manfaatlarini eng yuqori darajada ijobiy himoya qilish.')}
              </p>
            </div>

            <div className="p-8 rounded-xl bg-[#090e18] border border-[#1b2b46] hover:border-[#c59b27]/80 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c59b27]/12 transition-all duration-300 group relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-28 h-28 bg-[#c59b27]/0 group-hover:bg-[#c59b27]/15 rounded-full blur-2xl transition-all duration-500 pointer-events-none" />
              <ShieldCheck className="w-8 h-8 text-[#c59b27] mb-4 group-hover:scale-120 group-hover:-translate-y-1 transition-all duration-300" />
              <h3 className="font-serif font-bold text-slate-100 text-lg mb-2 group-hover:text-amber-200 transition-colors">
                {t('pillar_confidentiality', 'Maxfiylik Kafolati')}
              </h3>
              <p className="text-slate-400 text-xs leading-relaxed font-light">
                {t('pillar_confidentiality_desc', 'Advokatlik siri va barcha hujjatlar, suhbatlar daxlsizligi to‘liq himoyalangan va kafolatlangan.')}
              </p>
            </div>

            <div className="p-8 rounded-xl bg-[#090e18] border border-[#1b2b46] hover:border-[#c59b27]/80 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c59b27]/12 transition-all duration-300 group relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-28 h-28 bg-[#c59b27]/0 group-hover:bg-[#c59b27]/15 rounded-full blur-2xl transition-all duration-500 pointer-events-none" />
              <Award className="w-8 h-8 text-[#c59b27] mb-4 group-hover:scale-120 group-hover:-translate-y-1 transition-all duration-300" />
              <h3 className="font-serif font-bold text-slate-100 text-lg mb-2 group-hover:text-amber-200 transition-colors">
                {t('pillar_results', 'Amaliy Natija')}
              </h3>
              <p className="text-slate-400 text-xs leading-relaxed font-light">
                {t('pillar_results_desc', 'Mavhum tavsiyalar emas, balki real sud va shartnoma amaliyotida ishlaydigan strategiyalar.')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
